import { ThreeBounce } from 'better-react-spinkit';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { IoMdCloseCircle } from 'react-icons/io';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { DialogContext } from '../../store/context/DialogContext';
import { useTranslation } from '../../store/context/TranslationContext';
import CountrySelectService from '../CSC/CountrySelect/CountrySelectService';
import { SelectField } from '../SelectField/SelectField';
import AddressForm, { isAddressValid } from './AddressForm';
import { withComputedHidePinCode } from '../../pages/Form/components/PincodeField/helper';
import pincodeService from '../../pages/Form/components/PincodeField/PincodeService';
import "./stripe.css"
/* --------------------------- layout helper ------------------------ */
const ModalHeaderCentered = ({ children, onClose }) => (
    <ModalHeader
        className="w-100 border-0 justify-content-center"
    >
        <div className="">
            <span style={{
                width: '100%',
                textAlign: 'center',
                fontWeight: 600,
                fontSize: '24px',
            }}>
                {children}
            </span>
            <IoMdCloseCircle
                onClick={onClose}
                style={{

                    cursor: 'pointer',
                    fontSize: '28px',
                    color: '#939393',
                    position: 'absolute',
                    right: '20px',
                    top: '20px'
                }}
            />
        </div>
    </ModalHeader>
);

/* ------------------------------------------------------------------ */
const EMPTY_ADDRESS = {
    street: '',
    buildingNumber: '',
    city: '',
    state: '',
    pinCode: '',
    country: '',
};

const AddDebitCardModal = observer(
    ({ businessPlusData, onClose, existingCartItems, isFree, handleOrderCard }) => {
        const { t } = useTranslation();
        const { showError } = useContext(DialogContext);

        const [loading, setLoading] = useState(false);
        const [cardData, setCardData] = useState({
            individual: null,
            addressOption: null, // either a predefined address or { value:'CUSTOM_ADDRESS' }
        });
        const [customAddress, setCustomAddress] = useState(EMPTY_ADDRESS);
        const [errors, setErrors] = useState({});

        const { COUNTRY_MASTER_KEYS } = CountrySelectService;

        const {
            SANCTION_COUNTRY: sanctionCountryKey,
            COUNTRY : countryKey,
            VISA_CARD : visaCardKey
        } = COUNTRY_MASTER_KEYS;


        const fmtAddress = useCallback(
            (addr = {}) =>
                [addr.street, addr.buildingNumber, addr.city, addr.state, CountrySelectService.cyclosCountryMasterMap[addr.country]?.[countryKey], addr.pinCode]
                    .filter(Boolean)
                    .join(', '),
            []
        );

        /* Lists ---------------------------------------------------------- */
        const individualOptions = useMemo(() => {
            const inCart = existingCartItems.map((i) => i?.individualData?._id).filter(Boolean);
            return (businessPlusData?.individuals || [])
                .filter((ind) => !inCart.includes(ind._id) && ind?.isAuthorizedUser)
                .map((ind) => ({
                    ...ind,
                    label: `${ind?.firstName ?? ""} ${ind?.middleName ?? ""} ${ind?.lastName ?? ""}`.trim(),
                    value: ind._id,
                }));
        }, [businessPlusData, existingCartItems]);

        const addressOptions = useCallback(
            (individualId) => {
                const ind = businessPlusData?.individuals?.find((i) => i._id === individualId);

                const rawList = [
                    businessPlusData?.address,
                    ...(businessPlusData?.addressList || []),
                    ind?.address,
                ].filter(Boolean);

                const uniqueList = Array.from(
                    new Map(
                        rawList.map((addr) => [fmtAddress(addr).toLowerCase(), addr]) // case-insensitive
                    ).values()
                );

                const options = uniqueList.map((a) => ({
                    ...a,
                    label: fmtAddress(a),
                    value: fmtAddress(a),
                }));

                options.push({
                    value: 'CUSTOM_ADDRESS',
                    label: t('Custom Address'),
                    custom: true,
                });

                return options;
            },
            [businessPlusData, fmtAddress, t]
        );


        /* Validation ----------------------------------------------------- */
        const validateForm = (customAddr = customAddress) => {
            const errs = {};
            if (!cardData.individual) errs.individual = t('Please select an individual');
            if (!cardData.addressOption) errs.address = t('Please select an address');

            const usingCustom = cardData.addressOption?.custom;

            if (usingCustom) {
                if (!isAddressValid(customAddr)) errs.customAddress = t('Please enter a valid address');
            }
            setErrors(errs);
            return Object.keys(errs).length === 0;
        };

        /* Submit --------------------------------------------------------- */
        const handleSubmit = async () => {
            // Heal before validate/submit: a country with a fixed postal code (e.g. HK 999077) hides
            // the Zip field, so a stored/empty pinCode must be back-filled from the country default -
            // otherwise a hidden field dead-ends validation and the backend rejects the empty value.
            // fetch() is cached (no-op once loaded); withComputedHidePinCode also corrects a stale
            // hidePinCode flag from the current country's default.
            await pincodeService.fetch().catch(() => {});
            const usingCustom = cardData.addressOption?.custom;
            const healedCustom = usingCustom ? withComputedHidePinCode(customAddress) : null;
            if (usingCustom) setCustomAddress(healedCustom);

            if (!validateForm(usingCustom ? healedCustom : customAddress)) return;
            setLoading(true);
            const addressPayload = usingCustom ? healedCustom : withComputedHidePinCode(cardData.addressOption);

            if (
                CountrySelectService?.cyclosCountryMasterMap?.[addressPayload?.country]?.[visaCardKey] == "No"
                || CountrySelectService?.cyclosCountryMasterMap?.[addressPayload?.country]?.[sanctionCountryKey] == "Yes"
            ) {
                const error = t(`We are currently not able to deliver a card to this address. Please retry with another country's address.`);
                setErrors((e) => ({ ...e, customAddress: error }));
                setLoading(false);
                return
            }

            try {
                await handleOrderCard({
                    individual_id: cardData.individual.value,
                    address: addressPayload,
                    isFree,
                });
                onClose();
            } catch (e) {
                showError(e.message);
            } finally {
                setLoading(false);
            }
        };

        /* flags */
        const showCustomAddress = cardData.addressOption?.custom;

        /* -- RENDER ------------------------------------------------------ */
        return (
            <Modal size='lg' isOpen toggle={onClose} centered className="custom-modal">
                <ModalHeaderCentered onClose={onClose}>
                    <div>{t(' Add Card for')}</div>
                    <div>{t('Business User')}</div>
                </ModalHeaderCentered>

                <ModalBody>
                    {/* Individual ------------------------------------------------ */}
                    <SelectField
                        value={cardData.individual?._id}
                        error={errors.individual}
                        showErr={!!errors.individual}
                        data={individualOptions}
                        label={t('Available Business User')}
                        placeholder={t('Choose User')}
                        className="mb-3"
                        onChange={(val) => {
                            setErrors((e) => ({ ...e, individual: null }));
                            setCardData({ individual: val, addressOption: null });
                            setCustomAddress(EMPTY_ADDRESS);
                        }}
                    />

                    {/* Address --------------------------------------------------- */}
                    <SelectField
                        value={cardData.addressOption?.value}
                        error={errors.address}
                        showErr={!!errors.address}
                        data={addressOptions(cardData.individual?._id)}
                        label={t('Shipping Address')}
                        placeholder={t('Select Address')}
                        isDisabled={!cardData.individual}
                        onChange={(val) => {
                            setErrors((e) => ({ ...e, address: null, customAddress: null }));
                            setCardData((prev) => ({ ...prev, addressOption: val }));
                        }}
                    />

                    {/* Custom Address form -------------------------------------- */}
                    {showCustomAddress && (
                        <AddressForm
                            address={customAddress}
                            onChange={(v) => {
                                setErrors((e) => ({ ...e, customAddress: null }));
                                setCustomAddress((prev) => ({
                                    ...prev,
                                    ...v
                                }));
                            }}
                            showErrors={!!errors.customAddress}
                            disabled={loading}
                            className="mt-3"
                        />
                    )}


                    {errors.customAddress && <Alert className='mb-0 mt-2 py-1' color="danger" role="alert">
                        <div className="d-flex justify-content-between align-items-center">
                            {errors.customAddress}
                        </div>
                    </Alert>}


                </ModalBody>

                <ModalFooter>
                    <div className="d-flex w-100 justify-content-between align-items-center">
                        <Button className="form-content-btn-outline" onClick={onClose}>
                            {t('Cancel')}
                        </Button>
                        <Button
                            className="form-content-btn mt-3"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? <ThreeBounce color="white" size={8} /> : t('Save Order')}
                        </Button>
                    </div>
                </ModalFooter>
            </Modal>
        );
    }
);

export default (AddDebitCardModal);
